<template>
  <div class="flex h-screen bg-[#F8F9FA] font-sans antialiased overflow-hidden text-[#1A1A1A]">
    
    <aside class="w-64 bg-white border-r border-gray-200 flex flex-col shrink-0">
      <div class="p-6">
        <button class="w-full py-3 bg-red-500 text-white rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg shadow-red-200 hover:bg-red-600 transition">
          Tickets Urgents ({{ urgentCount }})
        </button>
      </div>

      <nav class="flex-1 px-4 space-y-1">
        <div class="text-[10px] font-black text-gray-400 uppercase px-3 mb-2 tracking-widest">Vues</div>
        <button v-for="f in filters" :key="f.label"
                @click="setFilter(f.status)"
                :class="['w-full flex justify-between items-center px-3 py-2 text-sm font-bold rounded-lg transition group',
                         activeFilter === f.status ? 'bg-blue-50 text-blue-600' : 'hover:bg-gray-50 text-gray-600 hover:text-black']">
          <span>{{ f.label }}</span>
          <span class="bg-gray-100 text-gray-500 px-2 py-0.5 rounded-md text-[10px]">{{ f.count }}</span>
        </button>
      </nav>
    </aside>

    <main class="flex-1 flex flex-col min-w-0">
      
      <header class="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-8 shrink-0">
        <div class="flex items-center flex-1">
          <svg class="w-4 h-4 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="3" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
          <input v-model="searchQuery" type="text" placeholder="Recherche rapide (ID, Client, Message...)" class="bg-transparent border-none text-sm w-full focus:ring-0">
        </div>
        <div class="flex gap-4">
          <div class="h-8 w-[1px] bg-gray-200"></div>
          <button class="text-[10px] font-black uppercase tracking-widest text-gray-400 hover:text-black transition">Exporter CSV</button>
        </div>
      </header>

      <div class="flex-1 overflow-auto bg-white">
        <table class="w-full border-collapse text-left relative">
          <thead class="sticky top-0 bg-white shadow-sm z-10">
            <tr class="text-[10px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-100">
              <th class="pl-8 py-4 w-10">#ID</th>
              <th class="px-4 py-4 w-32">Statut</th>
              <th class="px-4 py-4 w-40">Client</th>
              <th class="px-4 py-4">Message</th>
              <th class="px-4 py-4 w-24 text-right">Priorité</th>
              <th class="px-8 py-4 w-40 text-right">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-50">
            <tr v-for="ticket in filteredTickets" :key="ticket.id"
                class="hover:bg-blue-50/30 transition-colors group cursor-pointer"
                :class="{'opacity-60 bg-gray-50/50': ticket.status === 'done'}">

              <td class="pl-8 py-4 font-mono text-[11px] text-gray-400">{{ ticket.id }}</td>

              <td class="px-4 py-4">
                <select v-model="ticket.status"
                        @change="updateTicketStatus(ticket, ticket.status)"
                        :class="ticket.status === 'done' ? 'text-green-600 bg-green-50' : 'text-orange-600 bg-orange-50'"
                        class="text-[9px] font-black uppercase border-none rounded-md py-1 px-2 focus:ring-0 cursor-pointer">
                  <option value="open">Ouvert</option>
                  <option value="in-progress">En cours</option>
                  <option value="done">Terminé</option>
                </select>
              </td>

              <td class="px-4 py-4">
                <div class="text-xs font-black truncate">{{ ticket.client }}</div>
                <div class="text-[10px] text-gray-400 font-medium">{{ ticket.time }}</div>
              </td>

              <td class="px-4 py-4">
                <div class="text-xs text-gray-600 truncate max-w-md font-medium">
                  <span class="font-bold text-black mr-2">{{ ticket.subject }} —</span>
                  {{ ticket.message }}
                </div>
              </td>

              <td class="px-4 py-4 text-right">
                <span :class="priorityBadge(ticket.priority)" class="px-2 py-1 rounded text-[9px] font-black uppercase">
                  {{ ticket.priority }}
                </span>
              </td>

              <td class="px-8 py-4 text-right">
                <div class="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button @click="openReply(ticket)" class="p-2 bg-gray-900 text-white rounded-lg hover:bg-black transition">
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" /></svg>
                  </button>
                  <button @click="quickClose(ticket)" class="p-2 bg-white border border-gray-200 text-gray-400 rounded-lg hover:text-green-600 hover:border-green-600 transition">
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="3" d="M5 13l4 4L19 7" /></svg>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </main>

    <div v-if="activeTicket" class="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div class="bg-white w-full max-w-xl rounded-[2rem] shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
        <div class="p-8 border-b border-gray-50 flex justify-between items-start">
          <div>
            <span class="text-[10px] font-black text-gray-400 uppercase">Répondre au ticket #{{ activeTicket.id }}</span>
            <h3 class="text-xl font-black text-gray-900">{{ activeTicket.subject }}</h3>
          </div>
          <button @click="activeTicket = null" class="text-gray-400 hover:text-black">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <div class="p-8 bg-gray-50">
          <div class="text-sm text-gray-600 mb-6 bg-white p-4 rounded-xl border border-gray-100">
            {{ activeTicket.message }}
          </div>
          <textarea v-model="replyText" rows="4" placeholder="Votre réponse officielle..." 
                    class="w-full border-none bg-white rounded-2xl p-4 text-sm shadow-sm focus:ring-2 focus:ring-black mb-4"></textarea>
          <div class="flex gap-3">
            <button @click="submitReply" class="flex-1 py-4 bg-gray-900 text-white rounded-2xl text-[10px] font-black uppercase tracking-[0.2em]">Envoyer & Clôturer</button>
            <button @click="submitReplyOnly" class="px-6 py-4 bg-white border border-gray-200 rounded-2xl text-[10px] font-black uppercase">Répondre uniquement</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { toast } from 'vue3-toastify';

export default {
  data() {
    return {
      activeTicket: null,
      replyText: '',
      urgentCount: 0,
      searchQuery: '',
      activeFilter: 'all',
      filters: [
        { label: 'Tous les tickets', count: 0, status: 'all' },
        { label: 'Ouverts', count: 0, status: 'open' },
        { label: 'En cours', count: 0, status: 'in-progress' },
        { label: 'Archives', count: 0, status: 'done' },
      ],
      tickets: [],
      stats: {},
      selectedAgencyId: null
    }
  },

  computed: {
    /**
     * Filtre les tickets dynamiquement selon le statut sélectionné 
     * et la recherche textuelle.
     */
    filteredTickets() {
      let filtered = this.tickets;

      if (this.activeFilter !== 'all') {
        filtered = filtered.filter(t => t.status === this.activeFilter);
      }

      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(t =>
          t.ticketId?.toLowerCase().includes(query) ||
          t.client?.toLowerCase().includes(query) ||
          t.subject?.toLowerCase().includes(query) ||
          t.message?.toLowerCase().includes(query)
        );
      }

      return filtered;
    }
  },

  mounted() {
    this.fetchTickets();
    this.fetchStats();
    this.setupWebSocket();
  },

  methods: {
    getHeaders() {
      return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('superAdminToken')}`
      };
    },

    /**
     * Récupère la liste des tickets et normalise les données
     */
    async fetchTickets() {
      try {
        const params = new URLSearchParams({ limit: 100 });
        if (this.selectedAgencyId) {
          params.append('agencyId', this.selectedAgencyId);
        }

        console.log('[Ticket] Chargement des tickets...');
        const res = await fetch(`https://api.datafuse.fr/api/super-admin/support-tickets?${params}`, {
          headers: this.getHeaders()
        });

        if (!res.ok) {
          console.error('[Ticket] Erreur chargement:', res.status);

          if (res.status === 401 || res.status === 403) {
            toast.error('Session expirée. Reconnexion nécessaire.');
            localStorage.removeItem('superAdminToken');
            this.$router.push('/login');
            return;
          }

          throw new Error(`Erreur ${res.status}`);
        }

        const data = await res.json();
        console.log('[Ticket] Données reçues:', data);

        const ticketsList = data.tickets || data; // Gère les deux formats de réponse possibles

        this.tickets = ticketsList.map(t => ({
          ...t,
          id: t.ticketId || t._id, // Prendre ticketId ou _id
          // Normalise le client pour la recherche et l'affichage
          client: typeof t.client === 'object' ? t.client.name : (t.client || 'Client inconnu'),
          time: this.formatTime(t.createdAt)
        }));

        console.log('[Ticket] ✅ Tickets chargés:', this.tickets.length);
        this.updateLocalCounts();
      } catch (err) {
        console.error('[Ticket] Erreur fetch tickets:', err);
        toast.error(`Erreur de chargement: ${err.message}`);
      }
    },

    /**
     * Recalcule les compteurs de filtres basés sur les données chargées
     */
    updateLocalCounts() {
      this.filters[0].count = this.tickets.length;
      this.filters[1].count = this.tickets.filter(t => t.status === 'open').length;
      this.filters[2].count = this.tickets.filter(t => t.status === 'in-progress').length;
      this.filters[3].count = this.tickets.filter(t => t.status === 'done').length;
      this.urgentCount = this.tickets.filter(t => t.priority === 'Critique').length;
    },

    /**
     * Récupère les statistiques globales depuis l'API
     */
    async fetchStats() {
      try {
        // Optionnel : ne fetch que si une agence est sélectionnée, ou changer d'URL pour le global
        const url = this.selectedAgencyId 
          ? `https://api.datafuse.fr/api/super-admin/support-tickets/stats/${this.selectedAgencyId}`
          : `https://api.datafuse.fr/api/super-admin/support-tickets/stats/global`;

        const res = await fetch(url, { headers: this.getHeaders() });

        if (res.ok) {
          this.stats = await res.json();
          this.urgentCount = this.stats.urgent || 0;
          this.filters[1].count = this.stats.open || 0;
          this.filters[2].count = this.stats.inProgress || 0;
          this.filters[3].count = this.stats.done || 0;
        }
      } catch (err) {
        console.warn('Erreur fetch stats (peut être normal si route non créée):', err);
      }
    },

    /**
     * Met à jour le statut d'un ticket (PATCH recommandé pour la mise à jour partielle)
     */
    async updateTicketStatus(ticket, newStatus) {
      try {
        // Utiliser _id en priorité, sinon id
        const ticketId = ticket._id || ticket.id;
        console.log('[Ticket] Mise à jour statut:', { ticketId, newStatus });

        const res = await fetch(`https://api.datafuse.fr/api/super-admin/support-tickets/${ticketId}`, {
          method: 'PUT',
          headers: this.getHeaders(),
          body: JSON.stringify({ status: newStatus })
        });

        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          console.error('[Ticket] Erreur réponse:', { status: res.status, errorData });

          if (res.status === 401 || res.status === 403) {
            toast.error('Session expirée. Reconnexion nécessaire.');
            localStorage.removeItem('superAdminToken');
            this.$router.push('/login');
            return;
          }

          throw new Error(errorData.error || `Erreur ${res.status}`);
        }

        ticket.status = newStatus;
        this.updateLocalCounts();
        toast.success(`Statut : ${newStatus}`);
        console.log('[Ticket] ✅ Statut mis à jour avec succès');
      } catch (err) {
        console.error('[Ticket] Erreur mise à jour:', err);
        toast.error(`Erreur: ${err.message || 'Erreur lors du changement de statut'}`);
      }
    },

    /**
     * Envoie une réponse et clôture le ticket
     */
    async submitReply() {
      await this.sendResponseLogic(true);
    },

    /**
     * Envoie une réponse sans changer le statut "terminé"
     */
    async submitReplyOnly() {
      await this.sendResponseLogic(false);
    },

    /**
     * Logique partagée pour l'envoi de réponses
     */
    async sendResponseLogic(shouldClose) {
      if (!this.replyText.trim()) return toast.error('Réponse vide');

      try {
        // Utiliser _id en priorité, sinon id
        const ticketId = this.activeTicket._id || this.activeTicket.id;
        console.log('[Ticket] Envoi réponse:', { ticketId, shouldClose, message: this.replyText });

        const res = await fetch(`https://api.datafuse.fr/api/super-admin/support-tickets/${ticketId}/responses`, {
          method: 'POST',
          headers: this.getHeaders(),
          body: JSON.stringify({ message: this.replyText })
        });

        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          console.error('[Ticket] Erreur envoi réponse:', { status: res.status, errorData });

          if (res.status === 401 || res.status === 403) {
            toast.error('Session expirée. Reconnexion nécessaire.');
            localStorage.removeItem('superAdminToken');
            this.$router.push('/login');
            return;
          }

          throw new Error(errorData.error || `Erreur ${res.status}`);
        }

        console.log('[Ticket] ✅ Réponse envoyée avec succès');

        if (shouldClose) {
          await this.updateTicketStatus(this.activeTicket, 'done');
          toast.success('Réponse envoyée et ticket clôturé');
        } else {
          toast.success('Réponse envoyée');
          this.fetchTickets();
        }

        this.activeTicket = null;
        this.replyText = '';
      } catch (err) {
        console.error('[Ticket] Erreur envoi:', err);
        toast.error(`Erreur: ${err.message || 'Erreur lors de l\'envoi'}`);
      }
    },

    /**
     * Badge CSS selon la priorité (doit correspondre aux enums Mongoose)
     */
    priorityBadge(p) {
      const map = {
        'Critique': 'bg-red-100 text-red-600',
        'Haute': 'bg-orange-100 text-orange-600',
        'Moyenne': 'bg-yellow-100 text-yellow-600',
        'Basse': 'bg-blue-100 text-blue-600'
      };
      return map[p] || 'bg-gray-100 text-gray-500';
    },

    formatTime(timestamp) {
      const date = new Date(timestamp);
      const now = new Date();
      const diff = Math.floor((now - date) / 1000);

      if (diff < 60) return `il y a ${diff}s`;
      if (diff < 3600) return `il y a ${Math.floor(diff / 60)}m`;
      if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
      return `il y a ${Math.floor(diff / 86400)}j`;
    },

    setupWebSocket() {
      // Optionnel : Intégration Socket.io ici
    },

    openReply(ticket) {
      this.activeTicket = ticket;
    },

    async quickClose(ticket) {
      await this.updateTicketStatus(ticket, 'done');
    },

    setFilter(status) {
      this.activeFilter = status;
    }
  }
}
</script>